import { ExternalLink } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, t } from "@/i18n/translations";

const Partners = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const { language } = useLanguage();

  const partners = [
    { name: "Office des Forages Ruraux", logo: "/OFOR_Logo.png", website: "#" },
    { name: "CETUD", logo: "/Cetud.png", website: "#" },
    { name: "EDE", logo: "/logo.svg", website: "https://cabinetede.com/fr" },
    { name: "SONES", logo: "/SONES.png", website: "http://www.sones.sn/" },
    { name: "UNAPP", logo: "/Logo_unappp_0.png", website: "https://ppp.gouv.sn/fr" },
    { name: "PNUD", logo: "/pnud-logo-blue.svg", website: "https://www.undp.org/fr" },
    { name: "Banque Mondiale", logo: "/BM.png", website: "https://ppp.gouv.sn/fr" },
    { name: "PEPAM", logo: "/LOGO_PEPAM.jpeg", website: "#" },
    { name: "Union des Comores", logo: "/ANPI.png", website: "#" },
  ];

  return (
    <section id="partners" className="py-24 bg-muted/30" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-[hsl(var(--brand-red))] text-sm font-semibold tracking-widest uppercase mb-3">{t(translations.partners.badge, language)}</span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">{t(translations.partners.title, language)}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t(translations.partners.subtitle, language)}
          </p>
        </motion.div>

        <Carousel
          plugins={[Autoplay({ delay: 3000 })]}
          opts={{ align: "start", dragThreshold: 0, loop: true }}
          className="w-full"
        >
          <CarouselContent className="-ml-3">
            {partners.map((partner, index) => (
              <CarouselItem key={index} className="pl-3 basis-1/2 sm:basis-1/3 lg:basis-1/4 xl:basis-1/5">
                <a
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col items-center gap-4 p-8 rounded-2xl bg-card border border-border hover:border-[hsl(var(--brand-red))/0.3] hover:shadow-[var(--shadow-card)] transition-all duration-300 h-full"
                >
                  <img
                    src={partner.logo}
                    alt={`${partner.name} logo`}
                    className="w-16 h-16 object-contain grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                  <span className="text-sm font-semibold text-foreground text-center">{partner.name}</span>
                </a>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[hsl(var(--brand-navy))] to-[hsl(var(--brand-red))] p-6 sm:p-10 text-white">
            <h3 className="text-xl sm:text-2xl font-bold mb-3">{t(translations.partners.ctaTitle, language)}</h3>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto text-sm sm:text-base">
              {t(translations.partners.ctaDesc, language)}
            </p>
            <a href="#contact" className="inline-flex items-center gap-2 bg-white text-[hsl(var(--brand-navy))] px-8 py-3 rounded-xl font-semibold hover:bg-white/90 transition-colors">
              {t(translations.partners.ctaButton, language)}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Partners;
